import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";
import { useMappingScanStore } from "../stores/mappingScanStore";
import { MappingLiveClient } from "../api/mappingLive";
import { useSiteStore } from "../stores/siteStore";

const POINT_COLOR = "#36D399"; // radar return (green on dark, legible on --op-blue-900)
const CART_COLOR = "#E6EEFC";
const RING_COLOR = "#1E5BD8";  // --op-blue-500
const SCOPE_RADIUS_M = 5;      // fixed scope half-extent (user spec: 5 m radar)
const RING_COUNT = 5;          // one ring per metre

// The cart sits at the centre of the scope: at the live pose when localized, else at the
// origin (the cloud arrives cart-relative in camera frame when not localized).
function cartCenter() {
  const pose = useMappingScanStore.getState().pose;
  return pose ? { x: pose.x, z: pose.z, yaw: pose.yaw } : { x: 0, z: 0, yaw: 0 };
}

// Renders the preallocated store buffer as THREE.Points; flips needsUpdate + drawRange
// on each store `version` bump (no per-frame allocation, no React reconciliation of points).
function ScanPoints() {
  const geomRef = useRef<THREE.BufferGeometry>(null);
  const positions = useMappingScanStore((s) => s.positions);
  const lastVersion = useRef(-1);

  useFrame(() => {
    const g = geomRef.current;
    if (!g) return;
    const { version, count } = useMappingScanStore.getState();
    if (version === lastVersion.current) return;
    lastVersion.current = version;
    const attr = g.getAttribute("position") as THREE.BufferAttribute;
    attr.needsUpdate = true;
    g.setDrawRange(0, count);
  });

  return (
    <points>
      <bufferGeometry ref={geomRef}>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      {/* Fixed-pixel dots: under the OrthographicCamera (50 m above), sizeAttenuation makes
          gl_PointSize sub-pixel (size 0.1 → invisible) — the reason the scope drew rings/cart
          but no returns while the readout said "receiving". Pixel-sized blips are what a scope
          wants anyway. */}
      <pointsMaterial color={POINT_COLOR} size={3.5} sizeAttenuation={false} />
    </points>
  );
}

// Static range rings (1..5 m) + crosshair, kept centred on the cart each frame.
function RangeRings() {
  const group = useRef<THREE.Group>(null);
  const rings = useMemo(() => {
    const out: Float32Array[] = [];
    for (let r = 1; r <= RING_COUNT; r++) {
      const seg = 72;
      const pts = new Float32Array((seg + 1) * 3);
      for (let i = 0; i <= seg; i++) {
        const a = (i / seg) * Math.PI * 2;
        pts[i * 3] = Math.cos(a) * r;
        pts[i * 3 + 2] = Math.sin(a) * r;
      }
      out.push(pts);
    }
    return out;
  }, []);
  const cross = useMemo(() => new Float32Array([
    -SCOPE_RADIUS_M, 0, 0, SCOPE_RADIUS_M, 0, 0,
    0, 0, -SCOPE_RADIUS_M, 0, 0, SCOPE_RADIUS_M,
  ]), []);

  useFrame(() => {
    const c = cartCenter();
    if (group.current) group.current.position.set(c.x, 0, c.z);
  });

  return (
    <group ref={group}>
      {rings.map((pts, i) => (
        <line key={i}>
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" args={[pts, 3]} />
          </bufferGeometry>
          <lineBasicMaterial color={RING_COLOR} transparent opacity={i === RING_COUNT - 1 ? 0.55 : 0.28} />
        </line>
      ))}
      <lineSegments>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[cross, 3]} />
        </bufferGeometry>
        <lineBasicMaterial color={RING_COLOR} transparent opacity={0.2} />
      </lineSegments>
    </group>
  );
}

// Cart glyph at the scope centre; rotates to the heading when localized.
function CartGlyph() {
  const ref = useRef<THREE.Mesh>(null);
  useFrame(() => {
    if (!ref.current) return;
    const c = cartCenter();
    ref.current.position.set(c.x, 0.1, c.z);
    ref.current.rotation.set(-Math.PI / 2, 0, -c.yaw);
  });
  return (
    <mesh ref={ref}>
      <coneGeometry args={[0.35, 0.9, 3]} />
      <meshBasicMaterial color={CART_COLOR} />
    </mesh>
  );
}

// Follows the cart and pins a fixed 5 m scale (north-up scope), regardless of localization.
function ScopeCamera() {
  const cam = useThree((s) => s.camera) as THREE.OrthographicCamera;
  const size = useThree((s) => s.size);
  useFrame(() => {
    if (!(cam instanceof THREE.OrthographicCamera)) return;
    const c = cartCenter();
    cam.position.set(c.x, 50, c.z);
    cam.lookAt(c.x, 0, c.z);
    const zoom = (Math.min(size.width, size.height) / 2) / SCOPE_RADIUS_M;
    if (cam.zoom !== zoom) {
      cam.zoom = zoom;
      cam.updateProjectionMatrix();
    }
  });
  return null;
}

// Live connection chip + readouts (HTML overlay; the cloud itself is the WebGL canvas).
// Ticks once a second so the "no signal" state appears when frames stop arriving.
function ScanReadout() {
  const { t } = useTranslation();
  const count = useMappingScanStore((s) => s.count);
  const hz = useMappingScanStore((s) => s.hz);
  const lastMs = useMappingScanStore((s) => s.lastFrameMs);
  // Tick `now` from the interval (not Date.now() in render) so the "no signal" state appears
  // when frames stop arriving, while keeping the render a pure function of state.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const age = lastMs ? now - lastMs : Infinity;
  const state: "receiving" | "waiting" | "noSignal" =
    !lastMs ? "waiting" : age > 3000 ? "noSignal" : "receiving";
  const dot = state === "receiving" ? "#16A34A" : state === "waiting" ? "#D97706" : "#DC2626";

  // Rendered OUTSIDE the circular scope: the round overflow clip used to cut the
  // corner-anchored chips off mid-word.
  return (
    <div style={{
      width: "100%", maxWidth: 340, display: "flex",
      justifyContent: "space-between", alignItems: "center",
      fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--op-ink-500)",
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot }} />
        {t(`mapping.live.${state}`)}
      </span>
      {state === "receiving" && (
        <span style={{ display: "inline-flex", gap: 14 }}>
          <span>{t("mapping.live.points")}: {count}</span>
          <span>{t("mapping.live.rate")}: {hz.toFixed(1).replace(".", ",")} Hz</span>
        </span>
      )}
    </div>
  );
}

export function MappingScanView({ buildId }: { buildId: string }) {
  const { t } = useTranslation();
  const reset = useMappingScanStore((s) => s.reset);
  const applyFrame = useMappingScanStore((s) => s.applyFrame);

  useEffect(() => {
    reset();
    const token = useSiteStore.getState().token ?? undefined;
    const client = new MappingLiveClient(buildId, token, applyFrame);
    client.connect();
    return () => { client.disconnect(); reset(); };
  }, [buildId, reset, applyFrame]);

  // Status above and scale below the scope — anything overlaid inside the circle
  // gets clipped by its round overflow mask.
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <ScanReadout />
      <div style={{
        position: "relative", width: "100%", maxWidth: 340, aspectRatio: "1",
        borderRadius: "50%", overflow: "hidden", background: "#05122E",
        border: "1px solid var(--op-blue-700, #0E2F6E)",
      }}>
        <Canvas>
          <OrthographicCamera makeDefault position={[0, 50, 0]} near={0.1} far={200} zoom={20} />
          <ScopeCamera />
          <RangeRings />
          <ScanPoints />
          <CartGlyph />
        </Canvas>
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--op-ink-500)" }}>
        {t("mapping.live.scale", { m: SCOPE_RADIUS_M })}
      </div>
    </div>
  );
}
